import { useMemo, useState } from "react";
import { Target, Zap, Square, Users, HeartPulse } from "lucide-react";

const TABS = [
  {
    id: "goals", label: "Marcatori", icon: Target, color: "#39FF14",
    sort: (p) => p.goals * 100 + p.shots_on_target * 3 + p.shots,
    cols: [["goals", "Gol"], ["shots", "Tiri"], ["shots_on_target", "In porta"], ["shots_per_app", "Tiri/gara"], ["sot_pct", "% in porta"], ["conversion_pct", "% conv."]],
  },
  {
    id: "assists", label: "Assist", icon: Zap, color: "#007AFF",
    sort: (p) => p.assists * 100 + p.goals,
    cols: [["assists", "Assist"], ["assists_per_app", "Assist/gara"], ["goals", "Gol"], ["fouls_suffered", "Falli subiti"]],
  },
  {
    id: "cards", label: "Ammonizioni", icon: Square, color: "#FFCC00",
    sort: (p) => p.yellow_cards * 100 + p.red_cards * 150 + p.fouls_committed,
    cols: [["yellow_cards", "Gialli"], ["red_cards", "Rossi"], ["fouls_committed", "Falli fatti"], ["fouls_per_app", "Falli/gara"], ["yellow_per_app", "Gialli/gara"], ["fouls_suffered", "Falli subiti"]],
  },
];

const fmt = (v, k = "") => (k.endsWith("_per_app") ? Number(v).toFixed(2) : Math.round(v));

function Table({ rows, tab, starters }) {
  const best = Object.fromEntries(tab.cols.map(([k]) => [k, Math.max(0, ...rows.map((r) => r[k] || 0))]));
  return (
    <div className="relative">
    <div className="sm:hidden absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-[#0f0f0f] to-transparent pointer-events-none z-10" />
    <div className="sm:hidden text-[10px] text-[#71717A] text-right mb-1">scorri per altre colonne →</div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[560px]" data-testid={`player-stats-table-${tab.id}`}>
        <thead>
          <tr className="text-left">
            <th className="label text-[9px] py-2 pr-2">Giocatore</th>
            <th className="label text-[9px] py-2 px-2 text-right">Pres.</th>
            {tab.cols.map(([k, l]) => <th key={k} className="label text-[9px] py-2 px-2 text-right whitespace-nowrap">{l}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} className="border-t border-[#1f1f22] hover:bg-[#141414] transition-colors" data-testid={`player-stat-row-${p.id}`}>
              <td className="py-2 pr-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="mono text-[10px] text-[#71717A] w-5 text-right shrink-0">{p.jersey || ""}</span>
                  <span className="font-medium truncate">{p.name}</span>
                  <span className="label text-[9px] shrink-0">{p.position}</span>
                  {starters.has(p.id) && <span className="text-[9px] font-black text-black bg-[#39FF14] px-1 shrink-0">TIT</span>}
                  {p.injuries.length > 0 && <HeartPulse size={12} className="text-[#FF3B30] shrink-0" />}
                </div>
                <div className="text-[10px] text-[#71717A] ml-7">{p.team}</div>
              </td>
              <td className="py-2 px-2 text-right mono text-[#A1A1AA]">{fmt(p.apps)}<span className="text-[#52525b]"> ({fmt(p.starts)})</span></td>
              {tab.cols.map(([k], i) => {
                const v = p[k] || 0;
                const top = v > 0 && v === best[k];
                return (
                  <td key={k} className={`py-2 px-2 text-right mono ${i === 0 ? "font-black text-base" : ""}`} style={{ color: top ? tab.color : i === 0 ? "#fff" : "#A1A1AA" }}>
                    {fmt(v, k)}{k.endsWith("_pct") ? "%" : ""}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </div>
  );
}

export default function PlayerStats({ match }) {
  const [tabId, setTabId] = useState("goals");
  const [side, setSide] = useState("both");
  const [onlyStarters, setOnlyStarters] = useState(false);
  const ps = match.player_stats;
  const tab = TABS.find((t) => t.id === tabId);

  const starters = useMemo(
    () => new Set([...(match.home_formation?.players || []), ...(match.away_formation?.players || [])].map((p) => p.id)),
    [match]
  );

  const rows = useMemo(() => {
    if (!ps) return [];
    const list = [];
    if (side !== "away") ps.home.players.forEach((p) => list.push({ ...p, team: match.home.name }));
    if (side !== "home") ps.away.players.forEach((p) => list.push({ ...p, team: match.away.name }));
    return list
      .filter((p) => p.apps > 0 && p.position !== "G" && (!onlyStarters || starters.has(p.id)))
      .sort((a, b) => tab.sort(b) - tab.sort(a))
      .slice(0, 20);
  }, [ps, side, onlyStarters, starters, tab, match]);

  const injured = useMemo(() => {
    if (!ps) return [];
    const all = [...ps.home.players.map((p) => ({ ...p, team: match.home.short })), ...ps.away.players.map((p) => ({ ...p, team: match.away.short }))];
    return all.filter((p) => p.injuries.length).map((p) => ({ ...p, injuryLabel: p.injuries.map((i) => i.status).filter(Boolean).join(", ") }));
  }, [ps, match]);

  if (!ps) return null;
  const comps = [...new Set([...ps.home.competitions, ...ps.away.competitions])].join(" + ");

  return (
    <div className="surface-raised p-6 space-y-5" data-testid="player-stats">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label flex items-center gap-2"><Users size={12} strokeWidth={1.5} /> Statistiche giocatori · dall&apos;inizio della stagione</div>
          <p className="text-xs text-[#71717A] mt-1">{comps} · Fonte ESPN · Pres. = presenze (da titolare). TIT = in formazione.</p>
        </div>
        <div className="flex gap-1" data-testid="player-stats-side">
          {[["both", "Entrambe"], ["home", match.home.short], ["away", match.away.short]].map(([id, l]) => (
            <button key={id} onClick={() => setSide(id)} data-testid={`player-stats-side-${id}`}
              className={`px-3 py-1.5 text-[11px] font-bold uppercase border transition-colors ${side === id ? "border-white text-white" : "border-[#27272A] text-[#71717A] hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTabId(t.id)} data-testid={`player-stats-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-wider border transition-colors ${tabId === t.id ? "text-black" : "border-[#27272A] text-[#A1A1AA] hover:text-white"}`}
            style={tabId === t.id ? { background: t.color, borderColor: t.color } : undefined}>
            <t.icon size={12} /> {t.label}
          </button>
        ))}
        <label className="ml-auto flex items-center gap-2 text-xs text-[#A1A1AA] cursor-pointer select-none">
          <input type="checkbox" checked={onlyStarters} onChange={(e) => setOnlyStarters(e.target.checked)} data-testid="player-stats-starters-only" className="accent-[#39FF14]" />
          Solo in formazione
        </label>
      </div>

      {rows.length ? <Table rows={rows} tab={tab} starters={starters} /> : <p className="text-sm text-[#A1A1AA]">Nessuna statistica disponibile.</p>}

      {injured.length > 0 && (
        <div data-testid="injured-players">
          <div className="label text-[10px] mb-2 flex items-center gap-2"><HeartPulse size={12} className="text-[#FF3B30]" /> Indisponibili</div>
          <div className="flex flex-wrap gap-2">
            {injured.map((p) => (
              <span key={p.id} className="border border-[#FF3B30]/40 px-2 py-1 text-xs">{p.name} <span className="text-[#71717A]">{p.team} · {p.injuryLabel}</span></span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
